import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MODULE_GROUPS } from "@/lib/modules";
import { useAuth } from "@/lib/auth";
import { Bell, Search, LogOut, ChevronDown, Menu, X, ShieldCheck } from "lucide-react";

const ACCENT_TEXT = { copper: "text-kcc-copper-light", cobalt: "text-kcc-cobalt-light", purple: "text-kcc-purple", green: "text-kcc-green" };

function Sidebar({ open, onClose }) {
    return (
        <aside
            data-testid="app-sidebar"
            className={`fixed lg:sticky top-0 z-40 h-screen w-72 bg-[#0a1128] border-r border-white/5 flex-shrink-0 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        >
            <div className="px-5 pt-6 pb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg, #b87333, #2d68c4)" }}>
                        <span className="font-display font-bold text-white">K</span>
                    </div>
                    <div>
                        <div className="font-display text-lg font-semibold tracking-tight leading-none">KCC<span className="text-kcc-copper-light">360</span><sup className="text-[9px] text-kcc-silver ml-0.5">™</sup></div>
                        <div className="kcc-label mt-1 text-[9.5px]">Kamoto Copper Company</div>
                    </div>
                </div>
                <button className="lg:hidden text-kcc-silver hover:text-white" onClick={onClose} data-testid="sidebar-close"><X className="w-5 h-5" /></button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 pb-5 space-y-4">
                {MODULE_GROUPS.map((group) => (
                    <div key={group.family}>
                        <div className={`kcc-label px-3 pb-2 ${ACCENT_TEXT[group.accent] || "text-kcc-silver"}`}>{group.label}</div>
                        <div className="space-y-0.5">
                            {group.items.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <NavLink
                                        key={item.id}
                                        to={item.path}
                                        end={item.path === "/"}
                                        data-testid={item.testid}
                                        onClick={onClose}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-3 py-2 rounded-md text-sm text-kcc-silver hover:text-white hover:bg-white/5 transition-colors ${isActive ? "kcc-nav-active" : ""}`
                                        }
                                    >
                                        <Icon className="w-4 h-4" />
                                        <span>{item.label}</span>
                                    </NavLink>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>

            <div className="px-4 py-4 border-t border-white/5">
                <div className="flex items-center gap-2 text-[10.5px] kcc-text-silver">
                    <ShieldCheck className="w-3.5 h-3.5 text-kcc-green" />
                    Zero-Trust · MFA-Ready · Audit-Enabled
                </div>
            </div>
        </aside>
    );
}

function Topbar({ user, onMenu, notifications = 0 }) {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [menu, setMenu] = useState(false);

    return (
        <header className="kcc-glass sticky top-0 z-30 border-b border-white/5" data-testid="app-topbar">
            <div className="h-16 px-4 md:px-6 flex items-center gap-4">
                <button className="lg:hidden text-kcc-silver hover:text-white" onClick={onMenu} data-testid="menu-open"><Menu className="w-5 h-5" /></button>

                <div className="hidden md:flex items-center gap-2 flex-1 max-w-xl">
                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-md px-3 py-1.5 w-full">
                        <Search className="w-4 h-4 kcc-text-silver" />
                        <input
                            placeholder="Search assets, work orders, documents, people…"
                            className="bg-transparent outline-none text-sm flex-1 placeholder:text-kcc-silver/60"
                            data-testid="global-search"
                        />
                        <kbd className="hidden md:inline text-[10px] kcc-text-dim border border-white/10 rounded px-1.5 py-0.5">⌘K</kbd>
                    </div>
                </div>
                <div className="flex-1 md:hidden" />

                <button className="relative text-kcc-silver hover:text-white p-2" data-testid="notifications-btn" onClick={() => navigate("/notifications")}>
                    <Bell className="w-5 h-5" />
                    {notifications > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 bg-kcc-red text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 ring-2 ring-[#0d1017]">
                            {notifications}
                        </span>
                    )}
                </button>

                <div className="relative">
                    <button className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-md hover:bg-white/5" onClick={() => setMenu((v) => !v)} data-testid="user-menu-btn">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-display font-semibold text-xs text-white" style={{ background: "linear-gradient(135deg, #b87333, #2d68c4)" }}>
                            {user?.avatar_initials || "U"}
                        </div>
                        <div className="hidden md:block text-left">
                            <div className="text-xs font-semibold text-white leading-tight">{user?.name}</div>
                            <div className="text-[10px] kcc-text-silver leading-tight">{user?.title || user?.role}</div>
                        </div>
                        <ChevronDown className="w-3.5 h-3.5 kcc-text-silver" />
                    </button>
                    {menu && (
                        <div className="absolute right-0 top-full mt-2 w-64 kcc-surface-elev shadow-2xl p-3 z-50" data-testid="user-menu">
                            <div className="p-2">
                                <div className="text-sm font-semibold text-white">{user?.name}</div>
                                <div className="text-[11px] kcc-text-silver">{user?.email}</div>
                                <div className="text-[11px] kcc-text-silver mt-0.5">{user?.department} · {user?.role}</div>
                            </div>
                            <div className="border-t border-white/5 my-1" />
                            <button
                                className="w-full text-left px-2 py-2 text-sm hover:bg-white/5 rounded flex items-center gap-2 text-kcc-red"
                                onClick={logout}
                                data-testid="logout-btn"
                            >
                                <LogOut className="w-4 h-4" /> Sign out
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}

export default function AppShell({ children, user, notifications = 0 }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="flex kcc-hex-bg min-h-screen">
            {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}
            <Sidebar open={open} onClose={() => setOpen(false)} />
            <div className="flex-1 min-w-0 flex flex-col">
                <Topbar user={user} onMenu={() => setOpen(true)} notifications={notifications} />
                <main className="flex-1 px-4 md:px-6 lg:px-8 py-6 md:py-8 max-w-[1600px] w-full mx-auto animate-fade-in">
                    {children}
                </main>
                <footer className="text-center text-[11px] kcc-text-dim py-6 border-t border-white/5">
                    KCC360™ · Fictional demonstration data · Not for operational use · © 2026 Kamoto Copper Company
                </footer>
            </div>
        </div>
    );
}
